'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import InputField from '@src/components/atoms/textfield/inputField';
import SearchIcon from '@src/components/icons/search';
import Cross from '@src/components/icons/cross';
import LoadIcon from '@src/components/icons/loadIcon';
import { DRepCard } from '@src/components/seo/entityCard';
import {
  drepsHref,
  type DRepStatusFilter,
} from '@src/components/seo/drepFilters';
import { canonicalDRepPath } from '@src/lib/seo/metadata';
import { drepDisplayName } from '@src/lib/seo/jsonLd';
import { avatarUrl } from '@src/lib/seo/image';
import {
  normalizeDRep,
  readHighlights,
  type DRepSearchHit,
} from '@src/lib/seo/drepRecord';
import environments from '@src/configs/environments';

/**
 * The directory's search, live.
 *
 * Two render paths on purpose. The server resolves `?q=` and hands the first
 * result set in as `initialHits`, so the page arrives searched — shareable,
 * indexable, and working without JavaScript. After that, typing queries the
 * same endpoint from the browser, which is what the navbar autocomplete has
 * always done; a submit-and-navigate round trip per keystroke would have been
 * the wrong way to get there.
 *
 * The URL is kept in sync with `history.replaceState` rather than a router
 * push: the address bar stays copyable without re-rendering the page on the
 * server for every character.
 */
export default function DRepDirectorySearch({
  initialQuery,
  initialHits,
  status,
  children,
}: {
  initialQuery: string;
  initialHits: DRepSearchHit[];
  status: DRepStatusFilter;
  /** The server-rendered directory listing, shown when there is no query. */
  children: React.ReactNode;
}) {
  const [term, setTerm] = useState(initialQuery);
  const [hits, setHits] = useState(initialHits);
  const [isSearching, setIsSearching] = useState(false);
  const latestTermRef = useRef(initialQuery);

  const runSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        try {
          const response = await fetch(
            `${environments.BASE_API_URL}/search?query=${encodeURIComponent(query)}`
          );
          const results = await response.json();
          // A slow response for an earlier term must not replace a newer one.
          if (latestTermRef.current !== query) return;
          setHits(
            (Array.isArray(results) ? results : [])
              .map((entry: unknown) => {
                const record = normalizeDRep(entry);
                return record
                  ? { record, highlights: readHighlights(entry) }
                  : null;
              })
              .filter((hit): hit is DRepSearchHit => hit !== null)
          );
        } catch {
          if (latestTermRef.current === query) setHits([]);
        } finally {
          if (latestTermRef.current === query) setIsSearching(false);
        }
      }, 350),
    []
  );

  useEffect(() => () => runSearch.cancel(), [runSearch]);

  const apply = (value: string) => {
    const query = value.trim();
    setTerm(value);
    latestTermRef.current = query;

    // Shareable URL without a server round trip per keystroke.
    window.history.replaceState(
      null,
      '',
      drepsHref(status, query || undefined)
    );

    if (!query) {
      runSearch.cancel();
      setHits([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    runSearch(query);
  };

  const searching = latestTermRef.current.length > 0;

  return (
    <>
      <InputField
        inputfieldSize="medium"
        placeholder="Search DReps by name, ID, objectives or qualifications..."
        value={term}
        onChange={(event) => apply(event.target.value)}
        preActions={[
          <SearchIcon
            key="searchIcon"
            className="h-5 w-5 text-dark-neutral-900 dark:text-white"
          />,
        ]}
        postActions={
          term
            ? [
                isSearching ? (
                  <LoadIcon
                    key="searching"
                    className="animate-spin text-dark-neutral-900 dark:text-white"
                  />
                ) : (
                  <button
                    key="clear"
                    type="button"
                    aria-label="Clear search"
                    onClick={() => apply('')}
                    className="flex h-6 w-6 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-dark-neutral-500"
                  >
                    <Cross className="h-3 w-3" />
                  </button>
                ),
              ]
            : undefined
        }
        renderSeperators={false}
        className="w-full"
      />

      {searching ? (
        <>
          <p className="body14 border-t border-gray-200 pt-4 font-medium text-gray-600 dark:border-dark-neutral-700 dark:text-gray-400">
            {isSearching
              ? `Searching for “${latestTermRef.current}”…`
              : `${hits.length} best ${
                  hits.length === 1 ? 'match' : 'matches'
                } for “${latestTermRef.current}” — searched across every DRep, any status`}
          </p>

          {!isSearching && hits.length === 0 ? (
            <p className="body14 font-medium text-gray-600 dark:text-gray-400">
              Nothing in the DRep metadata index matches “
              {latestTermRef.current}”.
            </p>
          ) : (
            <ul
              className={`grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3 ${
                isSearching ? 'opacity-50' : ''
              }`}
            >
              {hits.map((hit) => (
                <li key={hit.record.drepId}>
                  <DRepCard
                    href={canonicalDRepPath(hit.record)}
                    name={drepDisplayName(hit.record)}
                    status={hit.record.status}
                    drepIdCip105={hit.record.drepIdCip105}
                    highlights={hit.highlights}
                    image={avatarUrl(hit.record)}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        children
      )}
    </>
  );
}
